import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Bell,
  CircleDollarSign,
  FileText,
  LayoutDashboard,
  ListOrdered,
  Mail,
  Moon,
  Package,
  PencilRuler,
} from "lucide-react";

import { getNameEquivalents } from "../../shared/numerology";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

type RequestStatus = "created" | "processing" | "sent" | "failed";

type VisualHtmlEditorProps = {
  value: string;
  onChange: (next: string) => void;
};

function VisualHtmlEditor({ value, onChange }: VisualHtmlEditorProps) {
  const [mode, setMode] = useState<"visual" | "html">("visual");
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mode !== "visual") return;
    if (!ref.current) return;
    if (ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
  }, [value, mode]);

  const sync = () => {
    const html = ref.current?.innerHTML ?? "";
    onChange(html);
  };

  const exec = (cmd: string, cmdValue?: string) => {
    // execCommand is deprecated but still widely supported and lightweight.
    document.execCommand(cmd, false, cmdValue);
    // Wait for DOM update
    window.setTimeout(sync, 0);
  };

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant={mode === "visual" ? "default" : "outline"} className="rounded-full" onClick={() => setMode("visual")}>
          Görsel
        </Button>
        <Button type="button" size="sm" variant={mode === "html" ? "default" : "outline"} className="rounded-full" onClick={() => setMode("html")}>
          HTML
        </Button>

        {mode === "visual" ? (
          <div className="ml-1 flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("bold")}>
              Kalın
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("italic")}>
              İtalik
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("underline")}>
              Altı çizili
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("insertUnorderedList")}>
              Liste
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("insertOrderedList")}>
              Numaralı
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => {
                const url = window.prompt("Link adresi (https://...)");
                if (!url) return;
                exec("createLink", url);
              }}
            >
              Link
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("unlink")}>
              Link kaldır
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => exec("removeFormat")}>
              Biçim temizle
            </Button>
          </div>
        ) : null}
      </div>

      <style>{`
        .visual-report-editor {
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
          font-size: 13px;
          line-height: 1.65;
          color: rgba(0,0,0,.88);
        }

        .visual-report-editor h1 { font-size: 18px; margin: 14px 0 10px 0; font-weight: 800; }
        .visual-report-editor h2 { font-size: 15px; margin: 14px 0 8px 0; font-weight: 800; }
        .visual-report-editor h3 { font-size: 13px; margin: 12px 0 6px 0; font-weight: 800; }
        .visual-report-editor p { margin: 0 0 10px 0; }

        .visual-report-editor table { width: 100%; border-collapse: collapse; }
        .visual-report-editor td, .visual-report-editor th {
          border: 1px solid rgba(0,0,0,.12);
          padding: 6px 8px;
          vertical-align: top;
        }

        /* report-specific blocks */
        .visual-report-editor .summary {
          width: 100%;
          border: 1px solid rgba(0,0,0,.15);
          border-radius: 8px;
          overflow: hidden;
          background: rgba(255,255,255,.92);
          margin: 0 0 16px 0;
          font-size: 13px;
        }

        .visual-report-editor .summary td { border-bottom: 1px solid rgba(0,0,0,.10); }
        .visual-report-editor .summary tr:last-child td { border-bottom: 0; }
        .visual-report-editor .summary td:first-child { color: rgba(0,0,0,.70); width: 58%; }
        .visual-report-editor .summary td:last-child { font-weight: 700; color: rgba(0,0,0,.88); }

        .visual-report-editor .name-grid table{ width: 100%; border-collapse: collapse; }
        .visual-report-editor .name-grid td{
          padding: 7px 4px;
          text-align: center;
          font-weight: 700;
          border-right: 1px solid rgba(0,0,0,.12);
        }
        .visual-report-editor .name-grid tr:last-child td{ font-weight: 600; color: rgba(0,0,0,.65); }
        .visual-report-editor .name-grid td:last-child{ border-right: 0; }
      `}</style>

      {mode === "html" ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-72 w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-100"
        />
      ) : (
        <div
          ref={ref}
          className="visual-report-editor min-h-[420px] w-full rounded-xl border border-slate-200 bg-white p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400/40 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-100"
          contentEditable
          suppressContentEditableWarning
          onInput={sync}
        />
      )}

      <div className="text-xs text-muted-foreground">
        Görsel mod: metne tıklayıp düzenleyin. Tablo/başlık görünümü raporla aynı olacak şekilde stil verildi.
      </div>
    </div>
  );
}

type RequestDetail = {
  ok: boolean;
  item: {
    id: string;
    createdAt: number;
    orderId: string;
    packageKey: string;
    reportLang: string;
    status: RequestStatus;
    ad: string;
    soyad: string;
    email: string;
    gun?: number;
    ay?: number;
    yil?: number;
    partnerAd?: string;
    partnerSoyad?: string;
    partnerGun?: number;
    partnerAy?: number;
    partnerYil?: number;
    deliverySpeed?: string;
    mailStatus?: string;
    mailScheduledAt?: number;
    mailAttemptCount?: number;
    mailLastAttemptAt?: number;
    downloadToken?: string;

    sentAt?: number;
    lastError?: string;
    reportHtml?: string;
  };
};

function formatDate(ts?: number) {
  if (!ts) return "-";
  return new Date(ts * 1000).toLocaleString();
}

async function fetchDetail(id: string): Promise<RequestDetail> {
  const res = await adminFetch(`/api/admin/numeroloji/requests/${encodeURIComponent(id)}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Detay alınamadı.");
  }
  return json as RequestDetail;
}

async function postAction(path: string) {
  const res = await adminFetch(`/api/admin/numeroloji${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "İşlem başarısız.");
  }
  return json as any;
}

async function postActionJson(path: string, body: unknown) {
  const res = await adminFetch(`/api/admin/numeroloji${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "İşlem başarısız.");
  }
  return json as any;
}

export default function AdminNumerolojiDetail() {
  const params = useParams();
  const id = String(params.id ?? "").trim();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-numeroloji-request", id],
    queryFn: () => fetchDetail(id),
    enabled: authenticated && Boolean(id),
  });

  type EditFormState = {
    ad: string;
    soyad: string;
    email: string;
    packageKey: string;
    reportLang: string;
    gun: string;
    ay: string;
    yil: string;
    partnerAd: string;
    partnerSoyad: string;
    partnerGun: string;
    partnerAy: string;
    partnerYil: string;
  };

  const [formSeed, setFormSeed] = useState<EditFormState | null>(null);
  const [form, setForm] = useState<EditFormState>({
    ad: "",
    soyad: "",
    email: "",
    packageKey: "normal",
    reportLang: "tr",
    gun: "",
    ay: "",
    yil: "",
    partnerAd: "",
    partnerSoyad: "",
    partnerGun: "",
    partnerAy: "",
    partnerYil: "",
  });

  const [reportEditOpen, setReportEditOpen] = useState(false);
  const [reportDraft, setReportDraft] = useState("");


  useEffect(() => {
    if (!data?.item) return;

    if (!reportEditOpen) {
      setReportDraft(data.item.reportHtml ?? "");
    }

    const seeded: EditFormState = {
      ad: data.item.ad ?? "",
      soyad: data.item.soyad ?? "",
      email: data.item.email ?? "",
      packageKey: data.item.packageKey ?? "normal",
      reportLang: data.item.reportLang ?? "tr",
      gun: data.item.gun ? String(data.item.gun) : "",
      ay: data.item.ay ? String(data.item.ay) : "",
      yil: data.item.yil ? String(data.item.yil) : "",
      partnerAd: data.item.partnerAd ?? "",
      partnerSoyad: data.item.partnerSoyad ?? "",
      partnerGun: data.item.partnerGun ? String(data.item.partnerGun) : "",
      partnerAy: data.item.partnerAy ? String(data.item.partnerAy) : "",
      partnerYil: data.item.partnerYil ? String(data.item.partnerYil) : "",
    };

    setFormSeed(seeded);
    setForm(seeded);
  }, [data?.item, reportEditOpen]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const numberOrUndef = (value: string) => {
        const trimmed = value.trim();
        return trimmed ? Number(trimmed) : undefined;
      };

      const stringOrUndef = (value: string) => {
        const trimmed = value.trim();
        return trimmed ? trimmed : undefined;
      };

      const payload: Record<string, unknown> = {
        ad: form.ad.trim(),
        soyad: form.soyad.trim(),
        email: form.email.trim(),
        packageKey: form.packageKey,
        reportLang: form.reportLang,
        gun: numberOrUndef(form.gun),
        ay: numberOrUndef(form.ay),
        yil: numberOrUndef(form.yil),
      };

      if (form.packageKey === "iliski") {
        payload.partnerAd = stringOrUndef(form.partnerAd);
        payload.partnerSoyad = stringOrUndef(form.partnerSoyad);
        payload.partnerGun = numberOrUndef(form.partnerGun);
        payload.partnerAy = numberOrUndef(form.partnerAy);
        payload.partnerYil = numberOrUndef(form.partnerYil);
      }

      await postActionJson(`/requests/${encodeURIComponent(id)}/update`, payload);
      await postAction(`/requests/${encodeURIComponent(id)}/prepare`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-request", id] });
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const prepareMutation = useMutation({
    mutationFn: () => postAction(`/requests/${encodeURIComponent(id)}/prepare`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-request", id] });
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const upgradeToPremiumMutation = useMutation({
    mutationFn: async () => {
      await postActionJson(`/requests/${encodeURIComponent(id)}/update`, { packageKey: "premium" });
      await postAction(`/requests/${encodeURIComponent(id)}/prepare`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-request", id] });
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const [sendBusy, setSendBusy] = useState(false);


  const blobToBase64 = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("PDF okunamadı."));
      reader.onload = () => {
        const result = typeof reader.result === "string" ? reader.result : "";
        const base64 = result.includes(",") ? result.split(",").pop() ?? "" : result;
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });

  const sendMutation = useMutation({
    mutationFn: (body: { pdfBase64: string }) => postActionJson(`/requests/${encodeURIComponent(id)}/send`, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-request", id] });
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const saveReportMutation = useMutation({
    mutationFn: async () => {
      await postActionJson(`/requests/${encodeURIComponent(id)}/report/update`, { reportHtml: reportDraft });
    },
    onSuccess: async () => {
      setReportEditOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-request", id] });
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => postAction(`/requests/${encodeURIComponent(id)}/delete`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] });
      navigate("/admin/numeroloji", { replace: true });
    },
  });

  const generatePdfBlob = async () => {
    // Server-side PDF generation (Puppeteer/Chromium) for consistent layout + best text sharpness.
    const res = await adminFetch(`/api/admin/numeroloji/requests/${encodeURIComponent(id)}/report.pdf`);
    if (!res.ok) {
      const json = (await res.json().catch(() => null)) as any;
      throw new Error(typeof json?.error === "string" ? json.error : "PDF oluşturulamadı.");
    }

    return await res.blob();
  };

  const downloadPdf = async (opts?: { returnBlob?: boolean }) => {
    const blob = await generatePdfBlob();
    if (opts?.returnBlob) return blob;

    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = `numeroloji-${id}.pdf`;
      a.click();
    } finally {
      URL.revokeObjectURL(url);
    }

    return blob;
  };

  const downloadPdfMutation = useMutation({
    mutationFn: () => downloadPdf(),
  });

  const reportSrcDoc = (() => {
    const item = data?.item;
    if (!item?.reportHtml) return "";

    let inner = item.reportHtml;

    if (item.packageKey !== "iliski" && !inner.includes("name-grid")) {
      const equivalents = getNameEquivalents(item.ad ?? "", item.soyad ?? "");
      if (equivalents.length) {
        const escapeHtml = (value: string) =>
          value
            .split("&")
            .join("&amp;")
            .split("<")
            .join("&lt;")
            .split(">")
            .join("&gt;")
            .split('"')
            .join("&quot;")
            .split("'")
            .join("&#039;");

        const letters = equivalents.map((c) => `<td>${escapeHtml(c.char)}</td>`).join("");
        const numbers = equivalents.map((c) => `<td>${escapeHtml(String(c.value))}</td>`).join("");

        const grid = `
<div class="name-grid">
  <table>
    <tbody>
      <tr>${letters}</tr>
      <tr>${numbers}</tr>
    </tbody>
  </table>
</div>
`.trim();

        inner = `${grid}\n\n${inner}`;
      }
    }

    return `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: dejavusans, "DejaVu Sans", ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    font-size: 12px;
    line-height: 1.6;
    color: #111;
    background: #fbfaff;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
  }

  .summary {
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
    margin: 0 0 16px 0;
    font-size: 12px;
  }

  .summary table { width: 100%; border-collapse: collapse; }
  .summary td { padding: 7px 9px; border: 1px solid #e6ddff; vertical-align: top; }
  .summary tr:nth-child(even) td { background: #faf7ff; }
  .summary td:first-child { font-weight: 800; background: #f4f0ff; width: 52%; }
  .summary td:last-child { font-weight: 700; color: rgba(0,0,0,.88); }

  .name-grid{
    width: 100%;
    overflow: hidden;
    border-radius: 10px;
    border: 1px solid #e6ddff;
    background: #ffffff;
    margin: 0 0 12px 0;
  }

  .name-grid table{ width: 100%; border-collapse: collapse; }

  .name-grid td{
    padding: 6px 6px;
    text-align: center;
    font-weight: 800;
    border-right: 1px solid #e6ddff;
    font-size: 10px;
  }

  .name-grid tr:first-child td { background: rgba(109,40,217,.06); }
  .name-grid tr:last-child td{ font-weight: 700; color: rgba(0,0,0,.78); background: rgba(109,40,217,.03); }
  .name-grid td:last-child{ border-right: 0; }

  .report-html {
    font-size: 12px;
    line-height: 1.45;
    color: rgba(0,0,0,.88);
    padding: 12px;
  }

  .report-html h1,
  .report-html h2,
  .report-html h3,
  .report-html h4,
  .report-html h5,
  .report-html h6 {
    margin: 10px 0 6px 0;
    font-weight: 800;
    line-height: 1.25;
    color: #111;
  }

  .report-html h1 { font-size: 24px; }
  .report-html h2 { font-size: 20px; }
  .report-html h3 { font-size: 17px; }
  .report-html h4 { font-size: 15px; }
  .report-html h5 { font-size: 13px; }
  .report-html h6 { font-size: 12px; }

  .report-html p { margin: 0 0 9px 0; }

  .report-html table { width: 100%; border-collapse: collapse; }
  .report-html td, .report-html th { border: 1px solid #e6ddff; padding: 7px 9px; vertical-align: top; }
  .report-html th { background: #f4f0ff; text-align: left; font-weight: 800; }
</style>
</head>
<body>
  <div class="report-html">${inner}</div>
</body>
</html>`;
  })();

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  if (!id) {
    return (
      <Card className="p-5">
        <div className="text-sm text-muted-foreground">Geçersiz istek.</div>
      </Card>
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar
        mode="links"
        isDark={isDark}
        onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
      />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex items-center gap-3">
            <NumerolojiAdminMobileSidebar
              mode="links"
              isDark={isDark}
              onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
            />
            <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">İstek detayı</div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
              aria-label="Bildirimler"
            >
              <Bell className="h-4 w-4" />
            </button>

            <div className="hidden items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40 sm:flex">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-orange-100 text-sm font-semibold text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                A
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">Admin Panel</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Süper Yetkili</div>
              </div>
            </div>

            <Button
              variant="outline"
              className="rounded-full"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Çıkış
            </Button>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="mx-auto grid w-full max-w-3xl gap-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-sm text-muted-foreground">#{id}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => navigate("/admin/numeroloji")}>Liste</Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  disabled={prepareMutation.isPending}
                  onClick={() => prepareMutation.mutate()}
                >
                  {prepareMutation.isPending ? "Hazırlanıyor..." : "Raporu hazırla"}
                </Button>
                <Button
                  className="rounded-full"
                  disabled={sendMutation.isPending || sendBusy}
                  onClick={async () => {
                    try {
                      setSendBusy(true);
                      const blob = (await downloadPdf({ returnBlob: true })) as unknown as Blob;
                      const pdfBase64 = await blobToBase64(blob);
                      await sendMutation.mutateAsync({ pdfBase64 });
                    } catch (e) {
                      const msg = e instanceof Error ? e.message : "Gönderim başarısız.";
                      globalThis.alert?.(msg);
                    } finally {
                      setSendBusy(false);
                    }
                  }}
                >
                  {sendBusy ? "PDF hazırlanıyor..." : sendMutation.isPending ? "Gönderiliyor..." : "Gönder"}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  disabled={downloadPdfMutation.isPending}
                  onClick={() => downloadPdfMutation.mutate()}
                >
                  {downloadPdfMutation.isPending ? "PDF hazırlanıyor..." : "PDF indir"}
                </Button>
                <Button
                  variant="destructive"
                  className="rounded-full"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    const ok = confirm("Bu isteği silmek istiyor musunuz?");
                    if (!ok) return;
                    deleteMutation.mutate();
                  }}
                >
                  Sil
                </Button>
              </div>

              {prepareMutation.isError ? (
                <div className="text-sm text-destructive">{(prepareMutation.error as Error).message}</div>
              ) : null}

              {sendMutation.isError ? (
                <div className="text-sm text-destructive">{(sendMutation.error as Error).message}</div>
              ) : null}

              {downloadPdfMutation.isError ? (
                <div className="text-sm text-destructive">{(downloadPdfMutation.error as Error).message}</div>
              ) : null}

            </div>

            <Card className="p-5">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Yükleniyor...</div>
        ) : error ? (
          <div className="text-sm text-destructive">{(error as Error).message}</div>
        ) : data ? (
          <div className="grid gap-4">
            <div>
              <div className="text-sm font-semibold">
                {data.item.ad} {data.item.soyad}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Paket: <span className="font-medium text-foreground">{data.item.packageKey}</span> • Dil: {data.item.reportLang} • Durum: {data.item.status}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">E-posta: {data.item.email}</div>
              <div className="mt-1 text-xs text-muted-foreground">Sipariş: {data.item.orderId}</div>

              {data.item.packageKey !== "premium" ? (
                <div className="mt-3 rounded-xl border bg-muted/20 p-3 text-sm">
                  <div className="font-semibold">Premium içerik görünmüyor</div>
                  <div className="mt-1 text-muted-foreground">
                    Öngörü / PIN kodları / PIN genel yorum sadece <span className="font-medium text-foreground">premium</span> pakette rapora eklenir.
                  </div>
                  <div className="mt-2">
                    <Button
                      size="sm"
                      className="rounded-full"
                      disabled={upgradeToPremiumMutation.isPending}
                      onClick={() => upgradeToPremiumMutation.mutate()}
                    >
                      {upgradeToPremiumMutation.isPending ? "Premium'a geçiriliyor..." : "Bu isteği Premium yap ve raporu hazırla"}
                    </Button>
                  </div>

                  {upgradeToPremiumMutation.isError ? (
                    <div className="mt-2 text-sm text-destructive">{(upgradeToPremiumMutation.error as Error).message}</div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="grid gap-2 rounded-xl border bg-muted/20 p-4 text-sm">
              <div className="grid gap-1 sm:grid-cols-2">
                <div>
                  <span className="text-muted-foreground">Oluşturulma:</span> {formatDate(data.item.createdAt)}
                </div>
                <div>
                  <span className="text-muted-foreground">Gönderildi:</span> {formatDate(data.item.sentAt)}
                </div>
              </div>

              {data.item.lastError ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-destructive">
                  {data.item.lastError}
                </div>
              ) : null}
            </div>

            <div className="grid gap-3 rounded-xl border bg-muted/20 p-4">
              <div className="text-sm font-semibold">Düzenle</div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label>Ad</Label>
                  <Input value={form.ad} onChange={(e) => setForm((p) => ({ ...p, ad: e.target.value }))} />
                </div>
                <div className="grid gap-1">
                  <Label>Soyad</Label>
                  <Input value={form.soyad} onChange={(e) => setForm((p) => ({ ...p, soyad: e.target.value }))} />
                </div>
                <div className="grid gap-1 sm:col-span-2">
                  <Label>E-posta</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                </div>

                <div className="grid gap-1">
                  <Label>Paket</Label>
                  <Select value={form.packageKey} onValueChange={(value) => setForm((p) => ({ ...p, packageKey: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">normal</SelectItem>
                      <SelectItem value="premium">premium</SelectItem>
                      <SelectItem value="isim">isim</SelectItem>
                      <SelectItem value="pin">pin</SelectItem>
                      <SelectItem value="iliski">iliski</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-1">
                  <Label>Dil</Label>
                  <Select value={form.reportLang} onValueChange={(value) => setForm((p) => ({ ...p, reportLang: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tr">tr</SelectItem>
                      <SelectItem value="en">en</SelectItem>
                      <SelectItem value="de">de</SelectItem>
                      <SelectItem value="es">es</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                  <div className="grid gap-1">
                    <Label>Doğum günü</Label>
                    <Input type="number" value={form.gun} onChange={(e) => setForm((p) => ({ ...p, gun: e.target.value }))} />
                  </div>
                  <div className="grid gap-1">
                    <Label>Ay</Label>
                    <Input type="number" value={form.ay} onChange={(e) => setForm((p) => ({ ...p, ay: e.target.value }))} />
                  </div>
                  <div className="grid gap-1">
                    <Label>Yıl</Label>
                    <Input type="number" value={form.yil} onChange={(e) => setForm((p) => ({ ...p, yil: e.target.value }))} />
                  </div>
                </div>

                {form.packageKey === "iliski" ? (
                  <>
                    <div className="grid gap-1">
                      <Label>Kişi B ad</Label>
                      <Input value={form.partnerAd} onChange={(e) => setForm((p) => ({ ...p, partnerAd: e.target.value }))} />
                    </div>
                    <div className="grid gap-1">
                      <Label>Kişi B soyad</Label>
                      <Input value={form.partnerSoyad} onChange={(e) => setForm((p) => ({ ...p, partnerSoyad: e.target.value }))} />
                    </div>

                    <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                      <div className="grid gap-1">
                        <Label>Kişi B gün</Label>
                        <Input type="number" value={form.partnerGun} onChange={(e) => setForm((p) => ({ ...p, partnerGun: e.target.value }))} />
                      </div>
                      <div className="grid gap-1">
                        <Label>Kişi B ay</Label>
                        <Input type="number" value={form.partnerAy} onChange={(e) => setForm((p) => ({ ...p, partnerAy: e.target.value }))} />
                      </div>
                      <div className="grid gap-1">
                        <Label>Kişi B yıl</Label>
                        <Input type="number" value={form.partnerYil} onChange={(e) => setForm((p) => ({ ...p, partnerYil: e.target.value }))} />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={
                    updateMutation.isPending ||
                    !form.ad.trim() ||
                    !form.soyad.trim() ||
                    !form.email.trim() ||
                    !form.email.includes("@")
                  }
                  onClick={() => updateMutation.mutate()}
                >
                  {updateMutation.isPending ? "Kaydediliyor..." : "Kaydet (ve raporu yenile)"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  disabled={!formSeed || updateMutation.isPending}
                  onClick={() => {
                    if (!formSeed) return;
                    setForm(formSeed);
                  }}
                >
                  Vazgeç
                </Button>
              </div>

              {updateMutation.isError ? (
                <div className="text-sm text-destructive">{(updateMutation.error as Error).message}</div>
              ) : null}
              {updateMutation.isSuccess ? <div className="text-sm text-emerald-600">Kaydedildi.</div> : null}
            </div>

            <div className="grid gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold">Rapor</div>

                {data.item.reportHtml ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => setReportEditOpen((v) => !v)}
                      disabled={saveReportMutation.isPending}
                    >
                      {reportEditOpen ? "Düzenlemeyi kapat" : "Raporu düzenle"}
                    </Button>

                    {reportEditOpen ? (
                      <>
                        <Button
                          size="sm"
                          className="rounded-full"
                          onClick={() => saveReportMutation.mutate()}
                          disabled={saveReportMutation.isPending}
                        >
                          {saveReportMutation.isPending ? "Kaydediliyor..." : "Kaydet"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => {
                            setReportDraft(data.item.reportHtml ?? "");
                            setReportEditOpen(false);
                          }}
                          disabled={saveReportMutation.isPending}
                        >
                          Vazgeç
                        </Button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {saveReportMutation.isError ? (
                <div className="text-sm text-destructive">{(saveReportMutation.error as Error).message}</div>
              ) : null}

              {data.item.reportHtml ? (
                <>
                  {reportEditOpen ? (
                    <div className="grid gap-2 rounded-xl border bg-muted/20 p-4">
                      <Label>Rapor düzenleme editörü</Label>

                      <VisualHtmlEditor value={reportDraft} onChange={setReportDraft} />

                      <div className="text-xs text-muted-foreground">
                        Bu editör raporun HTML içeriğini düzenler. “code” ile HTML görünümüne geçebilirsiniz.
                      </div>
                    </div>
                  ) : null}

                  <div className="overflow-hidden rounded-xl border">
                    {data.item.downloadToken ? (
                      <iframe
                        title="report"
                        sandbox=""
                        className="h-[520px] w-full bg-white"
                        src={`/api/numeroloji/requests/${encodeURIComponent(id)}/report.html?token=${encodeURIComponent(
                          data.item.downloadToken,
                        )}`}
                      />
                    ) : (
                      <iframe
                        title="report"
                        sandbox=""
                        className="h-[520px] w-full bg-white"
                        src={`/api/admin/numeroloji/requests/${encodeURIComponent(id)}/report.html`}
                      />
                    )}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">Henüz rapor hazırlanmadı. “Raporu hazırla” ile üretebilirsin.</div>
              )}
            </div>

          </div>
        ) : null}
            </Card>
          </div>
        </div>

      </div>
    </div>
  );
}
